/**
 * SEO Service - Manages dynamic SEO meta data for the Multi-Language Coding Learning App
 * 
 * This service handles:
 * - Dynamic page meta generation
 * - Lesson-specific SEO data
 * - Structured data schemas (Course, Website, Organization)
 * - Open Graph and Twitter card meta
 */

class SEOService {
  constructor() {
    this.seoConfig = null;
    this.brandingConfig = null;
    this.appConfig = null;
    this.baseUrl = '';
  }

  /**
   * Initialize the SEO service with configuration
   * @param {Object} config - The app configuration object containing seo, branding, and app sections
   */
  initialize(config) {
    this.seoConfig = config?.seo || {};
    this.brandingConfig = config?.branding || {};
    this.appConfig = config?.app || {};
    this.baseUrl = this.seoConfig.siteUrl || this.appConfig.website || window.location.origin;
  }

  /**
   * Get the Open Graph image URL
   * Priority: icon > splash
   * @returns {string|null} The full URL to the OG image or null if not available
   */
  getOgImage() {
    // Priority: icon > splash
    const iconPath = this.brandingConfig.icon;
    if (iconPath) {
      return `${this.baseUrl}/assets/media/${iconPath}`;
    }
    const splashPath = this.brandingConfig.splash;
    if (splashPath) {
      return `${this.baseUrl}/assets/media/${splashPath}`;
    }
    return null;
  }

  /**
   * Get meta data for a specific page
   * @param {string} pageName - The name of the page (e.g., 'home', 'lessons', 'challenges')
   * @param {Object} dynamicData - Dynamic data to interpolate into templates
   * @returns {Object} Meta data object with title, description, keywords, etc.
   */
  getPageMeta(pageName, dynamicData = {}) {
    const pageMeta = this.seoConfig?.pages?.[pageName] || {};
    return {
      title: this.interpolate(pageMeta.title || this.seoConfig.siteTitle, dynamicData),
      description: this.interpolate(pageMeta.description || this.seoConfig.siteDescription, dynamicData),
      keywords: this.seoConfig.keywords?.join(', ') || '',
      canonicalUrl: `${this.baseUrl}/${pageName === 'home' ? '' : pageName}`,
      ogImage: this.getOgImage(),
      twitterHandle: this.seoConfig.twitterHandle,
      locale: this.seoConfig.locale,
      author: this.seoConfig.author
    };
  }

  /**
   * Get meta data for a specific lesson
   * @param {Object} lesson - The lesson object containing title, description, id, etc.
   * @returns {Object} Meta data object with title, description, structured data, etc.
   */
  getLessonMeta(lesson) {
    const languageName = this.appConfig.language?.charAt(0).toUpperCase() + 
                         this.appConfig.language?.slice(1) || 'Programming';
    return {
      title: `${lesson.title} - Learn ${languageName}`,
      description: lesson.description || `Learn ${lesson.title} with interactive coding examples.`,
      keywords: `${lesson.title}, ${this.seoConfig.keywords?.slice(0, 3).join(', ')}`,
      canonicalUrl: `${this.baseUrl}/lessons/${lesson.id}`,
      ogImage: this.getOgImage(),
      structuredData: this.generateLessonSchema(lesson)
    };
  }

  /**
   * Generate Course structured data schema for a lesson
   * @param {Object} lesson - The lesson object
   * @returns {Object} JSON-LD structured data for the lesson
   */
  generateLessonSchema(lesson) {
    return {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": lesson.title,
      "description": lesson.description,
      "provider": {
        "@type": "Organization",
        "name": this.seoConfig.author || this.appConfig.author
      },
      "educationalLevel": lesson.difficulty || "Beginner"
    };
  }

  /**
   * Generate WebSite structured data schema
   * @returns {Object} JSON-LD structured data for the website
   */
  generateWebsiteSchema() {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": this.seoConfig.siteTitle,
      "url": this.baseUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${this.baseUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };
  }

  /**
   * Generate Organization structured data schema
   * @returns {Object} JSON-LD structured data for the organization
   */
  generateOrganizationSchema() {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": this.seoConfig.author || this.appConfig.author,
      "url": this.baseUrl,
      "logo": this.getOgImage()
    };
  }

  /**
   * Interpolate dynamic values into a template string
   * @param {string} template - The template string with {placeholder} syntax
   * @param {Object} data - The data object with values to interpolate
   * @returns {string} The interpolated string
   */
  interpolate(template, data) {
    if (!template) return '';
    return template.replace(/\{(\w+)\}/g, (match, key) => data[key] || match);
  }
}

export const seoService = new SEOService();
export default seoService;
